import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { AuthPayload } from '../components/page/SignIn/authFlow';

export interface UserModel {
  id: string;
  name?: string;
  email: string;
  roleId: string[];
  organizationId: string;
  status?: 'invited' | 'active' | 'inactive';
  emailVerified?: boolean;
  mfaEnabled?: boolean;
  mfaMethod?: { [key: string]: string };
  authorizedDevices?: string[];
  antiPhishingCode?: string;
  lastLoggedIn?: string;
  passwordChangedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  permissions?: string[];
  authorizedDevicesCount?: number;
  tenantId: string;
}

export type SSOType = 'saml' | 'partnerSSO';

export function useUserProfile(options?: { retry?: boolean }) {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: () =>
      api.get(`/api/base/users/me`).then((res) => res.data as UserModel),
    ...options,
  });
}

export function useUserLogin() {
  return useMutation({
    mutationFn: async (credentials: {
      email: string;
      password: string;
      token: string;
    }) =>
      api
        .post<{ data: AuthPayload }>('/api/base/auth/login', { ...credentials })
        .then((res) => res.data.data),
  });
}

export function useMfaVerify() {
  return useMutation({
    mutationFn: (token: string) =>
      api
        .post<{ data: AuthPayload }>(`/api/base/auth/mfa-verify`, { token })
        .then((res) => res.data.data),
  });
}
export function usePasswordChange() {
  return useMutation({
    mutationFn: (passwordChange: {
      newPassword: string;
      oldPassword: string;
    }) =>
      api
        .put<{
          data: AuthPayload;
        }>(`/api/base/auth/change-password`, passwordChange)
        .then((res) => res.data.data),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (passwordChange: {
      newPassword: string;
      resetToken?: string;
      invitationToken?: string;
      email: string;
      token: string;
    }) => api.put(`/api/base/auth/reset-password`, passwordChange),
  });
}

export function useMfaGenerate() {
  return useQuery({
    queryKey: ['MFAQuery'],
    queryFn: () =>
      api
        .get<{ mfaSecret: string }>(`/api/base/users/mfa`)
        .then((res) => res.data),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
    staleTime: 1000 * 60 * 60 * 24,
  });
}

export function useOrganizationInfo() {
  return useQuery({
    queryKey: ['organizationInfo'],
    queryFn: () =>
      api.get<Organization>(`/api/base/organization`).then((res) => res.data),
  });
}

export function useSSOLogin() {
  return useMutation({
    mutationFn: ({ token, ssoType }: { token: string; ssoType: string }) =>
      api
        .post<{
          data: AuthPayload;
        }>(`/api/base/auth/sso/login`, { token, ssoType })
        .then((res) => res.data.data),
  });
}

export function useSpInitSamlSSO() {
  return useMutation({
    mutationFn: ({
      email,
      token,
      relayState,
    }: {
      email: string;
      token: string;
      relayState?: string;
    }) =>
      api
        .post<{
          samlAuthRequestUrl: string;
        }>(`/api/base/auth/saml/sp-init`, { email, token, relayState })
        .then((res) => res.data),
  });
}

export const useSpInitSamlSsoByOrgSlug = () => {
  return useMutation({
    mutationFn: ({
      slug,
      token,
      relayState,
    }: {
      slug: string;
      token: string;
      relayState?: string;
    }) =>
      api
        .post<{
          samlAuthRequestUrl: string;
        }>(`/api/base/auth/saml/sp-init`, { slug, token, relayState })
        .then((res) => res.data),
  });
};

export function useForgotPassword() {
  return useMutation({
    mutationFn: ({ token, email }: { token: string; email: string }) =>
      api.post(`/api/base/auth/forgot-password`, { token, email }),
  });
}

export interface Organization {
  id: string;
  mfaPolicies?: MFAPolicy[];
  passwordPolicy: PasswordPolicy;
  sso?: SSO[];
}

export interface AuthenticatorMFA {
  type: 'authenticator';
  enforce: boolean;
}
export type MFAPolicy = AuthenticatorMFA;

export interface PasswordPolicy {
  minLength: number;
  lowercase?: boolean;
  uppercase?: boolean;
  numeric?: boolean;
  symbol?: boolean;
  restrictUserNamePart?: boolean;
  restrictFirstName?: boolean;
  restrictLastName?: boolean;
  restrictCommonPassword?: boolean;
  enforceHistory?: boolean;
  restrictLastPasswordCount?: number;
  minPasswordAge?: number;
  enforceMinPasswordAge?: boolean;
}

export interface SamlSSO {
  type: 'saml';
  certificate: string;
  idpId: string;
  url: string;
  updatedAt: string;
}
export type SSO = SamlSSO;

export interface SwitchTenant {
  tenantId: string;
}

export function useSwitchTenant() {
  return useMutation({
    mutationFn: (tenant: SwitchTenant) =>
      api.post(`/api/base/auth/switch-tenant`, tenant),
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: () => api.post(`/api/base/auth/logout`),
  });
}

export interface Tenant {
  tenantId: string;
  organizationId: string;
  name: string;
  status:
    | 'active'
    | 'failed'
    | 'in_progress'
    | 'delete_in_progress'
    | 'delete_failed';
  createdAt: string;
  updatedAt: string;
}

export function useGetCurrentTenant() {
  return useQuery({
    queryKey: ['tenant'],
    queryFn: async () => {
      return api
        .get<{ data: Tenant }>('/api/base/tenant')
        .then((res) => res.data.data);
    },
  });
}

export function useOrgTenants() {
  return useQuery({
    queryKey: ['listOrgTenants'],
    queryFn: () =>
      api
        .get<{ data: Tenant[] }>(`/api/base/tenants`)
        .then((res) => res.data.data),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 1000 * 60 * 2,
  });
}

export function useEmailLookup() {
  return useMutation({
    mutationFn: async ({ token, email }: { email: string; token: string }) => {
      return api
        .post<{
          regionURL: string;
          region?: string;
          isSSOUser?: boolean;
        }>(`/api/base/auth/lookup`, { token, email })
        .then(({ data }) => data);
    },
  });
}

export function useSignInOptions() {
  return useMutation({
    mutationFn: async ({ token, email }: { email: string; token: string }) => {
      return api
        .post<{
          sso: boolean;
          password: boolean;
        }>(`/api/base/auth/sign-in-options`, { token, email })
        .then(({ data }) => data);
    },
  });
}

export enum NotificationType {
  CRITICAL = 'critical',
  NON_CRITICAL = 'nonCritical',
}

export function useUnsubscribeNotificationEmail() {
  return useMutation({
    mutationFn: async ({
      token,
      type,
    }: {
      token: string;
      type: NotificationType;
    }) => {
      return api
        .post('/api/base/notifications/unsubscribe', { token, type })
        .then(({ data }) => data);
    },
  });
}

export interface OAuthParameters {
  code?: string | null;
  state?: string | null;
  error?: string | null;
  provider: string;
}

export interface OAuthCallbackResponse {
  userRegionUri: string;
  token?: string;
  data?: AuthPayload;
}

export function useOAuthCallback() {
  return useMutation({
    mutationFn: async ({ code, state, provider, error }: OAuthParameters) => {
      if (error) {
        throw new Error(`Error authenticating using ${provider}`, {
          cause: error,
        });
      }

      if (!code || !state) {
        throw new Error(`Error authenticating using ${provider}`);
      }

      return api
        .get<OAuthCallbackResponse>(
          `/api/base/oauth/${provider}/callback?code=${code}&state=${state}`,
        )
        .then((res) => res.data);
    },
  });
}

export function useCompleteOAuthSignup() {
  return useMutation({
    mutationFn: async ({
      code,
      recaptchaToken,
      provider,
    }: {
      code: string;
      recaptchaToken: string;
      provider: string;
    }) => {
      const response = await api.post<OAuthCallbackResponse>(
        `/api/base/oauth/${provider}/sign-up`,
        {
          code,
          recaptchaToken,
        },
      );
      return response.data;
    },
  });
}

export function useDefaultTenant() {
  return useQuery({
    queryKey: ['defaultTenant'],
    queryFn: () => {
      return localStorage.getItem('defaultTenant');
    },
  });
}
